import React from 'react';
import { Accordion } from '@agissc/ktag-map';
import { ConfigurationListItem } from './ConfigurationListItem';
import { UserMapConfiguration } from './api/useUserMapConfigurations';
import useAppState from '../../AppStore';
import { AccordionName } from '../../UIStateSlice';

interface DragHandleProps {
  listeners: Record<string, unknown>;
  attributes: Record<string, unknown>;
}

interface ConfigurationAccordionItemProps {
  configuration: UserMapConfiguration;
  isSelected: boolean;
  isDragOverlay: boolean;
  onApplyConfiguration: () => void;
  onUpdateConfiguration: (id: string, newName: string) => void;
  onRemoveConfiguration: () => void;
  dragHandleProps?: DragHandleProps;
}

const emptyDragHandleProps = { listeners: {}, attributes: {} };

export const ConfigurationAccordionItem = ({
  configuration,
  isSelected,
  isDragOverlay,
  onApplyConfiguration,
  onUpdateConfiguration,
  onRemoveConfiguration,
  dragHandleProps,
}: ConfigurationAccordionItemProps) => {
  const expanded = useAppState(
    (state) =>
      !!state.accordions[AccordionName.MAP_CONFIGURATION]?.find(
        (i) => i.id === configuration.id && i.expanded,
      ),
  );

  const setAccordionItem = useAppState((state) => state.setAccordionItem);

  const onToggle = (isExpanded: boolean) => {
    setAccordionItem(AccordionName.MAP_CONFIGURATION, configuration.id, isExpanded);
  };

  const getAdditionalClassNames = () => {
    const classes: string[] = ['configuration__accordion'];
    if (isDragOverlay) classes.push('accordion__dnd');
    if (isSelected) classes.push('configuration__item--active');
    return classes.join(' ');
  };

  const accordionProps = isDragOverlay
    ? { dragHandleProps: emptyDragHandleProps }
    : { onToggle, dragHandleProps };

  const handleApplyConfiguration = () => {
    if (!isDragOverlay) onApplyConfiguration();
  };

  const handleConfigurationChanged = (updated: UserMapConfiguration) => {
    if (!isDragOverlay) onUpdateConfiguration(configuration.id, updated.name);
  };

  const handleConfigurationRemoved = () => {
    if (!isDragOverlay) onRemoveConfiguration();
  };

  return (
    <Accordion
      key={`${configuration.id}-${expanded}`}
      title={configuration.name}
      openIcon="chevron-down"
      closedIcon="chevron-up"
      open={expanded}
      additionalClassNames={getAdditionalClassNames()}
      {...accordionProps}
    >
      <ConfigurationListItem
        configuration={configuration}
        isSelected={isSelected}
        onApplyConfiguration={handleApplyConfiguration}
        configurationChanged={handleConfigurationChanged}
        configurationRemoved={handleConfigurationRemoved}
      />
    </Accordion>
  );
};
